import React from "react";
import { cn } from "@/lib/utils";

/**
 * FormLayout — canonical form composition primitive.
 *
 * Provides consistent spacing between fields, label/field alignment,
 * submit-row placement, and error-summary slot.
 *
 * Composition:
 *   <FormLayout>
 *     <FormLayout.Field label="Email">
 *       <Input />
 *     </FormLayout.Field>
 *     <FormLayout.Field label="Password" error="Required">
 *       <Input type="password" />
 *     </FormLayout.Field>
 *     <FormLayout.Actions>
 *       <Button>Sign in</Button>
 *     </FormLayout.Actions>
 *   </FormLayout>
 *
 * a11y contract:
 *   * Each field wraps its label + control via `htmlFor`/id association.
 *   * Error text is announced via `role="alert"` when present.
 *   * `hint` is linked via `aria-describedby`.
 */

let _fieldIdSeq = 0;
function useAutoId(prefix = "ds-field") {
  const [id] = React.useState(() => {
    _fieldIdSeq += 1;
    return `${prefix}-${_fieldIdSeq}`;
  });
  return id;
}

function FormLayout({ children, onSubmit, className, "data-testid": testId, ...rest }) {
  return (
    <form
      data-testid={testId}
      onSubmit={onSubmit}
      noValidate
      className={cn("space-y-4", className)}
      {...rest}
    >
      {children}
    </form>
  );
}

function Field({ label, hint, error, required, htmlFor, children, className, "data-testid": testId }) {
  const autoId = useAutoId();
  const id = htmlFor || autoId;
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  // Attempt to clone the first React element child to inject id + aria refs
  // so consumers don't have to re-thread them by hand.
  const control = React.Children.map(children, (child, idx) => {
    if (idx !== 0 || !React.isValidElement(child)) return child;
    return React.cloneElement(child, {
      id: child.props.id || id,
      "aria-describedby": [hintId, errorId, child.props["aria-describedby"]].filter(Boolean).join(" ") || undefined,
      "aria-invalid": Boolean(error) || child.props["aria-invalid"] || undefined,
    });
  });
  return (
    <div data-testid={testId} className={cn("space-y-1.5", className)}>
      {label && (
        <label htmlFor={id} className="block text-[13px] font-medium text-[var(--ds-color-ink)]">
          {label}
          {required ? <span aria-hidden className="text-[var(--ds-color-danger)] ml-0.5">*</span> : null}
        </label>
      )}
      {control}
      {hint && !error && (
        <p id={hintId} className="text-[12px] text-[var(--ds-color-ink-muted)]">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} role="alert" className="text-[12px] text-[var(--ds-color-danger)]">
          {error}
        </p>
      )}
    </div>
  );
}

function Actions({ children, align = "right", className, "data-testid": testId }) {
  const alignClass =
    align === "left"
      ? "justify-start"
      : align === "between"
        ? "justify-between"
        : "justify-end";
  return (
    <div
      data-testid={testId}
      className={cn(
        "flex items-center gap-2 pt-4 border-t border-[var(--ds-color-border)]",
        alignClass,
        className,
      )}
    >
      {children}
    </div>
  );
}

function ErrorSummary({ errors, className, "data-testid": testId }) {
  const items = Array.isArray(errors) ? errors.filter(Boolean) : [];
  if (items.length === 0) return null;
  return (
    <div
      role="alert"
      data-testid={testId}
      className={cn(
        "rounded-[var(--ds-radius-md)] border border-[var(--ds-color-danger)] bg-[color-mix(in_srgb,var(--ds-color-danger)_8%,var(--ds-color-surface))] p-3 text-[13px] text-[var(--ds-color-danger)]",
        className,
      )}
    >
      <p className="font-medium mb-1">Please fix the following:</p>
      <ul className="list-disc ml-5 space-y-0.5">
        {items.map((msg, idx) => (
          <li key={idx}>{msg}</li>
        ))}
      </ul>
    </div>
  );
}

FormLayout.Field = Field;
FormLayout.Actions = Actions;
FormLayout.ErrorSummary = ErrorSummary;

export { FormLayout };
export default FormLayout;
